// The caller's reusable address book (GET /api/addresses/*).
//
// Today: the Company's saved ship-to (delivery) addresses, offered as a picker
// in the configurator's Shipping step so a customer (or an employee
// impersonating one) can reuse a known address instead of retyping it. The
// backend resolves the Company server-side from the verified token and returns
// a customer-safe subset — the SPA renders what comes back and never asks for
// or stores anything else.
//
// Not pricing data, so the SPA MAY cache it briefly (TanStack Query staleTime
// at the call site) — CLAUDE.md §Caching permits caching non-price company
// data.

import type { CompanyShipToAddress } from 'shared';

import { api } from './client.js';

export async function getShipToAddresses(): Promise<CompanyShipToAddress[]> {
  const res = await api.get<{ addresses: CompanyShipToAddress[] }>('/addresses/ship-to');
  return res.data.addresses;
}

// Remove one address from the Company's shared book. The backend refuses to
// remove the Company default (only the ERP owns that) with a validation error
// the caller surfaces. Returns the refreshed book so the caller re-renders
// without a second fetch.
export async function removeShipToAddress(addressId: string): Promise<CompanyShipToAddress[]> {
  const res = await api.delete<{ addresses: CompanyShipToAddress[] }>(
    `/addresses/ship-to/${encodeURIComponent(addressId)}`,
  );
  return res.data.addresses;
}

// One selectable country: display name + the iso2 code the wire carries.
export type ShipToCountry = { name: string; iso2: string };

// The canonical country list backing the ship-to country picker. Sourced from
// Nexus (via the backend) — the portal never types or invents a country; the
// picker binds each option's value to iso2.
export async function getShipToCountries(): Promise<ShipToCountry[]> {
  const res = await api.get<{ countries: ShipToCountry[] }>('/addresses/countries');
  return res.data.countries;
}
